import { Add, Remove } from 'components/icons';
import { InputGroupButton } from 'components/ui/input-group';
import { formatHoursToHm } from 'components/util/HoursDisplay';
import React from 'react';
import { FieldValues, SetFieldValue, useWatch } from 'react-hook-form';
import { t } from 'utils/getTranslations';
import {
    getIncrementDecimalScale,
    isOnIncrementGrid,
    roundUpToIncrement,
    stepDownByIncrement,
    stepUpByIncrement,
    useTimecardPrecision,
} from 'utils/useTimecardPrecision';
import TextInput, { TextInputProps } from './TextInput';

interface HoursInputProps<T extends FieldValues> extends TextInputProps<T> {
    useIncrementDecrementButtons?: boolean;
    setValue?: SetFieldValue<T>;
    /**
     * When set, the +/- buttons step along multiples of this increment and manual
     * entries are rounded up to the nearest multiple on blur, replacing the default
     * 0.1 stepping (and the quarter-hour snapping of two-decimal mode).
     */
    incrementStep?: number;
    /**
     * Shows a live hours-minutes equivalent of the entered decimal hours below the
     * field (e.g. 0.2 -> "12 min"), so time can be recorded without doing the math.
     */
    showMinutesHint?: boolean;
    /**
     * Ceiling for what can be entered: a keystroke or paste that would take the value
     * above it is rejected outright, the + button stops there, and the increment-grid
     * snap on blur cannot push past it. A value already above the ceiling when the form
     * loads is left alone — only new input is capped — so the field still needs its own
     * `validate` rule to catch that on submit.
     */
    maxValue?: number;
}

// Check if value is valid or could lead to a valid value in two-decimal mode
// Valid final values: X.0, X.10, X.20, X.25, X.30, X.40, X.50, X.60, X.70, X.75, X.80, X.90
const isValidTwoDecimalInput = (value: string): boolean => {
    const normalized = value.replace(',', '.');

    // Allow incomplete input
    if (/^\d{0,3}$/.test(normalized)) return true; // "1", "12"
    if (/^\d{0,3}\.$/.test(normalized)) return true; // "1."
    if (/^\d{0,3}\.[0-9]$/.test(normalized)) return true; // "1.2" (incomplete)
    if (/^\d{0,3}\.[0-9]0$/.test(normalized)) return true; // "1.20" (valid)
    if (/^\d{0,3}\.25$/.test(normalized)) return true; // "1.25" (valid)
    if (/^\d{0,3}\.75$/.test(normalized)) return true; // "1.75" (valid)

    return false;
};

const HoursInput = <T extends FieldValues>({
    useIncrementDecrementButtons = false,
    setValue,
    control,
    name,
    onChange,
    onBlur,
    disabled,
    incrementStep,
    showMinutesHint = false,
    maxValue,
    suffixComponent,
    ...props
}: HoursInputProps<T>) => {
    const currentValue = useWatch({ control, name });
    const { entryDecimalScale, isTwoDecimalEntryEnabled } = useTimecardPrecision();
    const hasIncrementOverride = incrementStep !== undefined && incrementStep > 0;
    const effectiveDecimalScale = hasIncrementOverride ? getIncrementDecimalScale(incrementStep) : entryDecimalScale;

    // NaN (an empty or half-typed value such as "" or ".") is never over the ceiling — clearing the
    // field and typing a decimal point must stay possible.
    const exceedsMax = (value: number) => maxValue !== undefined && !isNaN(value) && value > maxValue;
    const capToMax = (value: number) => (maxValue === undefined ? value : Math.min(value, maxValue));

    const transformEventBeforeChange = (
        e: React.ChangeEvent<HTMLInputElement>
    ): React.ChangeEvent<HTMLInputElement> | null => {
        const newValue = e?.target?.value?.replace(/,/g, '.') || '';

        if (isTwoDecimalEntryEnabled) {
            if (!isValidTwoDecimalInput(newValue)) {
                return null; // Block the change
            }
        }

        if (exceedsMax(parseFloat(newValue))) {
            return null; // Block the change
        }

        if (e?.target?.value) e.target.value = newValue;
        return e;
    };

    // Snap to next 0.1 interval when incrementing from quarter-hour values (0.25, 0.75)
    const snapToNextTenth = (value: number): number => {
        // Multiply by 10, ceiling, divide by 10 to get next 0.1 interval
        return Math.ceil(value * 10) / 10;
    };

    // Snap to previous 0.1 interval when decrementing from quarter-hour values (0.25, 0.75)
    const snapToPreviousTenth = (value: number): number => {
        // Multiply by 10, floor, divide by 10 to get previous 0.1 interval
        return Math.floor(value * 10) / 10;
    };

    const handleIncrement = () => {
        if (!setValue) return;

        const numValue = parseFloat(currentValue || '0');
        let newNumValue: number;

        if (hasIncrementOverride) {
            // On the increment grid: add one increment; off the grid: snap up to the nearest multiple
            newNumValue = stepUpByIncrement(numValue, incrementStep);
        } else if (isTwoDecimalEntryEnabled) {
            // Check if current value is at a quarter-hour (has .25 or .75 decimal part)
            const decimalPart = Math.round((numValue % 1) * 100);
            if (decimalPart === 25 || decimalPart === 75) {
                // Snap up to next 0.1 interval (e.g., 0.25 → 0.30, 0.75 → 0.80)
                newNumValue = snapToNextTenth(numValue);
            } else {
                newNumValue = numValue + 0.1;
            }
        } else {
            newNumValue = numValue + 0.1;
        }

        // Same rule as the blur snap: stepping up from a value already above the ceiling leaves it
        // where it is rather than pulling it down to the ceiling — a + press must never decrease it.
        const newValue = (exceedsMax(numValue) ? numValue : capToMax(newNumValue)).toFixed(effectiveDecimalScale);

        setValue(name, newValue, { shouldValidate: true, shouldDirty: true });

        if (onChange) {
            const syntheticEvent = {
                target: { value: newValue },
            } as React.ChangeEvent<HTMLInputElement>;
            onChange(syntheticEvent);
        }

        if (onBlur) {
            onBlur(newValue);
        }
    };

    const handleDecrement = () => {
        if (!setValue) return;

        const numValue = parseFloat(currentValue || '0');
        let newNumValue: number;

        if (hasIncrementOverride) {
            // On the increment grid: subtract one increment; off the grid: snap down to the nearest multiple
            newNumValue = stepDownByIncrement(numValue, incrementStep);
        } else if (isTwoDecimalEntryEnabled) {
            // Check if current value is at a quarter-hour (has .25 or .75 decimal part)
            const decimalPart = Math.round((numValue % 1) * 100);
            if (decimalPart === 25 || decimalPart === 75) {
                // Snap down to previous 0.1 interval (e.g., 0.25 → 0.20, 0.75 → 0.70)
                newNumValue = snapToPreviousTenth(numValue);
            } else {
                newNumValue = numValue - 0.1;
            }
        } else {
            newNumValue = numValue - 0.1;
        }

        const newValue = Math.max(0, newNumValue).toFixed(effectiveDecimalScale);

        setValue(name, newValue, { shouldValidate: true, shouldDirty: true });

        if (onChange) {
            const syntheticEvent = {
                target: { value: newValue },
            } as React.ChangeEvent<HTMLInputElement>;
            onChange(syntheticEvent);
        }

        if (onBlur) {
            onBlur(newValue);
        }
    };

    // Manual entries may land off the increment grid; on blur they are replaced with
    // the nearest multiple above (e.g. increment 0.2, entered 0.7 -> 0.8). A value already
    // above the ceiling is left untouched — snapping it would rewrite it down to the ceiling
    // on a bare focus-and-leave, destroying the very value the submit-time rule must report.
    const snapValueToIncrementGrid = (val?: string) => {
        if (hasIncrementOverride && setValue) {
            const numValue = parseFloat((val ?? '').replace(',', '.'));
            if (!isNaN(numValue) && !isOnIncrementGrid(numValue, incrementStep) && !exceedsMax(numValue)) {
                const newValue = capToMax(roundUpToIncrement(numValue, incrementStep)).toFixed(effectiveDecimalScale);

                setValue(name, newValue, { shouldValidate: true, shouldDirty: true });

                if (onChange) {
                    const syntheticEvent = {
                        target: { value: newValue },
                    } as React.ChangeEvent<HTMLInputElement>;
                    onChange(syntheticEvent);
                }

                if (onBlur) {
                    onBlur(newValue);
                }
                return;
            }
        }

        if (onBlur) {
            onBlur(val);
        }
    };

    const suffix =
        useIncrementDecrementButtons && setValue ? (
            <>
                {suffixComponent}
                <InputGroupButton
                    size="icon-xs"
                    onClick={handleDecrement}
                    disabled={disabled}
                    aria-label={t('decrement-hours')}
                >
                    <Remove fontSize="small" />
                </InputGroupButton>
                <InputGroupButton
                    size="icon-xs"
                    onClick={handleIncrement}
                    disabled={disabled}
                    aria-label={t('increment-hours')}
                >
                    <Add fontSize="small" />
                </InputGroupButton>
            </>
        ) : (
            suffixComponent
        );

    // Pattern for 1-decimal: allows up to 3 digits + 1 decimal
    // Pattern for 2-decimal: allows up to 3 digits + 2 decimals (validation is done in transformEventBeforeChange)
    const pattern = isTwoDecimalEntryEnabled ? '^(\\d{0,3}([\\.,]\\d{0,2})?)?$' : '^(\\d{0,3}([\\.,]\\d?)?)?$';

    const minutesHint = showMinutesHint
        ? formatHoursToHm(parseFloat((currentValue ?? '').toString().replace(/,/g, '.')))
        : '';

    const input = (
        <TextInput
            pattern={pattern}
            control={control}
            name={name}
            onChange={onChange}
            onBlur={snapValueToIncrementGrid}
            disabled={disabled}
            {...props}
            transformEventBeforeChange={transformEventBeforeChange}
            suffixComponent={suffix}
        />
    );

    if (!showMinutesHint) return input;

    return (
        // `form-field`: with its hint the field is still one cell of a record section's fields grid.
        <div className="form-field flex w-full flex-col">
            {input}
            {minutesHint ? (
                <span className="text-fg-secondary mt-0.5 text-xs whitespace-nowrap tabular-nums">{minutesHint}</span>
            ) : null}
        </div>
    );
};

export default HoursInput;
